// The event registry: every event the dashboard sends to PostHog, with the
// property keys it may carry and the kind of value each key holds.
//
// `enforceRegistry` runs last in `before_send`. It drops properties that are
// not declared for the event, drops string values that look like content (an
// email, an id, or a long free string) unless the key is declared as text,
// and drops any value of a closed vocabulary that is not one of its members.
// In warn mode it names each drop once. Adding an event is one EVENT_NAME
// member, one entry here, and one `track` or `capture` call that uses it.
//
// Every event name and every closed set of values is an enum below or in
// surfaceVocabulary.js. Code refers to members as NAME.KEY, never by their
// wire strings; scripts/analytics-vocabulary.test.mjs enforces that.
//
// Keys that start with `$` belong to the SDK (`$current_url`, `$lib`,
// `$elements_chain`, ...) and pass through untouched; URL-shaped ones are
// already reduced by `describeUrlState`. Events that start with `$` and are
// not listed here are SDK-owned as well and pass through whole.
//
// `url_<param>` keys are the router's query params flattened by
// `describeUrlStateStep`; they are allowed on every event as URL values.
import { AUTOCAPTURE_PROPERTY_VALUES } from "./attrs.js";
import { defineEnum } from "./enum.js";
import { CANVAS_IDS, DIALOG_IDS, DIMENSION_IDS, MODE_IDS, TAB_KINDS, UI_CONTROL_IDS, UNKNOWN_DIMENSION } from "./surfaceVocabulary.js";

export const EVENT_NAME = defineEnum("EVENT_NAME", {
    PAGEVIEW: "$pageview",
    PAGELEAVE: "$pageleave",
    AUTOCAPTURE: "$autocapture",
    ELEMENT_VIEWED: "$element_viewed",
    EXCEPTION: "$exception",
    RAGECLICK: "$rageclick",
    HEATMAP: "$$heatmap",
    IDENTIFY: "$identify",
    GROUP_IDENTIFY: "$groupidentify",
    CHAT_TURN: "chat_turn",
    UI_CONTROL_USED: "ui_control_used",
    API_REQUEST: "api_request",
    DATA_QUERY_FAILED: "data_query_failed",
    ANALYTICS_THROTTLED: "analytics_throttled",
});

// Value kinds:
//   STRING  - a short identifier the SDK or the router supplies
//   TEXT    - a short free string that is allowed to exceed the id checks
//   URL     - a URL or a query-param value, captured verbatim by decision
//   NUMBER, BOOLEAN, LIST (of short strings), ID (an opaque id we chose to send)
//   ONE_OF, LIST_OF - a member, or a list of members, of a closed vocabulary;
//                     built with oneOf() and listOf()
export const KIND = defineEnum("KIND", {
    STRING: "string",
    TEXT: "text",
    URL: "url",
    NUMBER: "number",
    BOOLEAN: "boolean",
    LIST: "list",
    ID: "id",
    ONE_OF: "one_of",
    LIST_OF: "list_of",
});

// Accepts an enum or a list of its values.
export function oneOf(members) {
    return Object.freeze({ kind: KIND.ONE_OF, values: new Set(Object.values(members)) });
}

export function listOf(members) {
    return Object.freeze({ kind: KIND.LIST_OF, values: new Set(Object.values(members)) });
}

export const CHAT_TRIGGER = defineEnum("CHAT_TRIGGER", {
    PROMPT: "prompt",
    SLASH: "slash",
    STARTER: "starter",
    CONTINUATION: "continuation",
});

export const CHAT_SURFACE = defineEnum("CHAT_SURFACE", {
    DOCKED: "docked",
    OVERLAY: "overlay",
});

export const REQUEST_ERROR_KIND = defineEnum("REQUEST_ERROR_KIND", {
    TIMEOUT: "timeout",
    ABORT: "abort",
    AUTH: "auth",
    NETWORK: "network",
});

// Abort is classified so it can be skipped; it is never sent.
export const QUERY_ERROR_KIND = defineEnum("QUERY_ERROR_KIND", {
    ABORT: "abort",
    AUTH: "auth",
    DENIED: "denied",
    NETWORK: "network",
    QUERY: "query",
    SERVER: "server",
    TIMEOUT: "timeout",
    UNKNOWN: "unknown",
});

// Values of the SDK's `$exception_source` property that the window listeners set.
export const EXCEPTION_SOURCE = defineEnum("EXCEPTION_SOURCE", {
    WINDOW_ERROR: "window.onerror",
    UNHANDLED_REJECTION: "unhandledrejection",
});

// The dashboard feature gates reported in the `gates` super property.
export const GATE = defineEnum("GATE", {
    SECURITY_TAB: "security_tab",
    COMPARISONS: "comparisons",
    INVENTORY: "inventory",
    SKILLS: "skills",
    CHAT_PERSISTENCE: "chat_persistence",
});

export const GROUP_TYPE = defineEnum("GROUP_TYPE", {
    TENANT: "tenant",
});

// Failure-class events can loop: a query that fails on every poll, or a
// render error that recurs on every frame, would otherwise send hundreds of
// billable events a minute. Each event here has a cap per window, and the
// first drop in a window sends one `analytics_throttled` marker so the burst
// is visible in PostHog without its volume.
export const EVENT_RATE_LIMITS = Object.freeze({
    [EVENT_NAME.DATA_QUERY_FAILED]: Object.freeze({ limit: 20, windowMs: 60_000 }),
    [EVENT_NAME.EXCEPTION]: Object.freeze({ limit: 10, windowMs: 60_000 }),
});

export const SHARED_PROPERTIES = Object.freeze({
    tenant_id: KIND.ID,
    // Display name beside the id so insights can break down by a readable
    // value without PostHog's group analytics add-on. Text, not string: a
    // tenant name may be long and carry digits, which reads as an id.
    tenant_name: KIND.TEXT,
    tenant_role: KIND.STRING,
    is_platform_admin: KIND.BOOLEAN,
    app_env: KIND.STRING,
    app_version: KIND.STRING,
    app_revision: KIND.STRING,
    gates: listOf(GATE),
    screen: KIND.STRING,
    mode: oneOf(MODE_IDS),
    view: KIND.STRING,
    range: KIND.STRING,
    lens: KIND.STRING,
    focus: oneOf(DIMENSION_IDS),
    scope_dims: listOf([...DIMENSION_IDS, UNKNOWN_DIMENSION]),
    scope_depth: KIND.NUMBER,
    tab_kinds: listOf(TAB_KINDS),
    tab_count: KIND.NUMBER,
    has_query: KIND.BOOLEAN,
    detail_open: KIND.BOOLEAN,
    canvas: oneOf(CANVAS_IDS),
});

export const EVENTS = Object.freeze({
    // `navigation_type` is the SDK's pushState, replaceState or popstate
    // marker on history-change pageviews. `title` is deliberately not
    // declared: the app sets the document title to the scope label.
    [EVENT_NAME.PAGEVIEW]: { navigation_type: KIND.STRING },
    [EVENT_NAME.PAGELEAVE]: {},
    // Autocapture text and attributes are masked; the only meaning a click
    // carries is the closed-vocabulary attribute set phAttrs() writes, which
    // the SDK lifts onto the event under these names.
    [EVENT_NAME.AUTOCAPTURE]: Object.fromEntries(
        Object.entries(AUTOCAPTURE_PROPERTY_VALUES).map(([name, values]) => [name, oneOf(values)]),
    ),
    [EVENT_NAME.ELEMENT_VIEWED]: { element_name: oneOf(DIALOG_IDS) },
    [EVENT_NAME.EXCEPTION]: {},
    // SDK-detected rage clicks and heatmap batches: pointer positions and
    // the masked element chain, no text.
    [EVENT_NAME.RAGECLICK]: {},
    [EVENT_NAME.HEATMAP]: {},
    [EVENT_NAME.IDENTIFY]: {},
    [EVENT_NAME.GROUP_IDENTIFY]: {},
    [EVENT_NAME.CHAT_TURN]: {
        trigger: oneOf(CHAT_TRIGGER),
        aborted: KIND.BOOLEAN,
        surface: oneOf(CHAT_SURFACE),
    },
    // A URL change that leaves the page the same: the controls whose params
    // changed. Made from the pageview the SDK sends for it (sanitize.js).
    [EVENT_NAME.UI_CONTROL_USED]: { controls: listOf(UI_CONTROL_IDS) },
    [EVENT_NAME.API_REQUEST]: {
        method: KIND.STRING,
        route: KIND.TEXT,
        status: KIND.NUMBER,
        ok: KIND.BOOLEAN,
        duration_ms: KIND.NUMBER,
        error_kind: oneOf(REQUEST_ERROR_KIND),
        // The allowlisted extra a call site may pass through `analytics` on
        // a rest helper. Counts only; never names.
        item_count: KIND.NUMBER,
    },
    [EVENT_NAME.DATA_QUERY_FAILED]: {
        error_kind: oneOf(QUERY_ERROR_KIND),
        error_name: KIND.STRING,
        entity: KIND.STRING,
        steps: KIND.NUMBER,
        duration_ms: KIND.NUMBER,
    },
    // Sent once per window when a rate-limited event exceeds its cap; the
    // burst is visible, its volume is not.
    [EVENT_NAME.ANALYTICS_THROTTLED]: {
        throttled_event: oneOf(Object.keys(EVENT_RATE_LIMITS)),
        limit: KIND.NUMBER,
        window_ms: KIND.NUMBER,
    },
});

function declaredKind(event, key) {
    return EVENTS[event]?.[key] ?? SHARED_PROPERTIES[key];
}

// The closed set of values a property may take on an event, or on any event
// when `event` is null; null when the property is not a closed vocabulary.
export function propertyValues(event, key) {
    const kinds = event
        ? [declaredKind(event, key)]
        : [SHARED_PROPERTIES[key], ...Object.values(EVENTS).map((props) => props[key])];
    const sets = kinds.filter((kind) => kind && typeof kind === "object").map((kind) => kind.values);
    return sets.length ? new Set(sets.flatMap((set) => [...set])) : null;
}

// Non-`$` keys the SDK itself puts on every event and checks for after
// `before_send`. Removing them makes the SDK drop the whole event.
const SDK_RESERVED = new Set(["token", "distinct_id"]);

const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Base64url or hex ids of 16 or more characters with at least one digit and
// no spaces. Origin ids ride the wire as base64; UUIDs and hashes match as
// well. Enum names such as ENTITY_TYPE_PROMPT carry no digit and pass.
const ID_LIKE = /^(?=.*\d)[A-Za-z0-9_=-]{16,}$/;
const MAX_STRING = 120;

function valueMatchesKind(value, kind) {
    if (kind && typeof kind === "object") {
        if (kind.kind === KIND.ONE_OF) return typeof value === "string" && kind.values.has(value);
        if (kind.kind === KIND.LIST_OF) return Array.isArray(value) && value.every((v) => typeof v === "string" && kind.values.has(v));
        return false;
    }
    switch (kind) {
        case KIND.NUMBER:
            return typeof value === "number" && Number.isFinite(value);
        case KIND.BOOLEAN:
            return typeof value === "boolean";
        case KIND.LIST:
            return Array.isArray(value)
                && value.every((v) => typeof v === "string" && v.length <= MAX_STRING && !EMAIL_LIKE.test(v));
        case KIND.ID:
            return typeof value === "string" && value.length <= MAX_STRING && !EMAIL_LIKE.test(value);
        case KIND.TEXT:
            return typeof value === "string" && value.length <= 300 && !EMAIL_LIKE.test(value);
        case KIND.URL:
            return typeof value === "string" && value.length <= 8192;
        case KIND.STRING:
            return typeof value === "string"
                && value.length <= MAX_STRING
                && !EMAIL_LIKE.test(value)
                && !ID_LIKE.test(value);
        default:
            return false;
    }
}

function describeKind(kind) {
    return kind && typeof kind === "object" ? `${kind.kind} ${[...kind.values].join(", ")}` : kind;
}

export function createRegistryEnforcer({ dev = false, warn = (...args) => console.warn(...args) } = {}) {
    const warned = new Set();
    const warnOnce = (key, message) => {
        if (!dev || warned.has(key)) return;
        warned.add(key);
        warn(`[analytics] ${message}`);
    };
    return function enforceRegistry(cr) {
        if (!cr || typeof cr.event !== "string") return cr;
        const declared = EVENTS[cr.event];
        if (!declared) {
            if (cr.event.startsWith("$")) return cr;
            warnOnce(`event:${cr.event}`, `dropped undeclared event "${cr.event}"; add it to lib/analytics/events.js`);
            return null;
        }
        const props = cr.properties;
        if (!props || typeof props !== "object") return cr;
        for (const key of Object.keys(props)) {
            if (key.startsWith("$") || SDK_RESERVED.has(key)) continue;
            const kind = declared[key] ?? SHARED_PROPERTIES[key] ?? (key.startsWith("url_") ? KIND.URL : undefined);
            if (!kind) {
                warnOnce(`${cr.event}:${key}`, `dropped undeclared property "${key}" on "${cr.event}"`);
                delete props[key];
                continue;
            }
            const value = props[key];
            if (value === null || value === undefined) continue;
            if (!valueMatchesKind(value, kind)) {
                warnOnce(`${cr.event}:${key}:value`, `dropped "${key}" on "${cr.event}": value does not fit ${describeKind(kind)}`);
                delete props[key];
            }
        }
        return cr;
    };
}
